import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import TkStepper from "../../components/atoms/TkStepper";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { TkGetCode } from "../../utils/TkGetCode";
import { DemanderKbn, Prefectures } from "../../assets/data/Constants";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { IAPI_RET005_Request } from "../../type/apiType";
import { httpAPI_RET005 } from "./api/request.formdata";
import { contextText, fakeTableList, schema, steps } from "./parts";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import { labelids } from "../../assets/data/TkExplanationProperty";
import { API_RET005_BuildReqParams } from "../../utils/buildRequestParams";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";
import _ from "lodash";

/**
 * SCRR411106 退職手当金の請求手続き（請求者情報の登録）
 * 退職手当金請求者が自身の情報を登録する。
 * @return SCRR411106
 */
const SCRR411106 = () => {
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.job_35;
  const userType = state.authInfo?.userType;
  const isHojin = userType === authorityUserType.userTypeNumber.KEIYAKUSYA;
  const isKiko = userType === authorityUserType.userTypeNumber.KIKO;
  const [stateTableList, setStateTableList] = useState<any>([]);
  const [open, setOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [emailWarningOpen, setEmailWarningOpen] = useState(false);
  const [tel, setTel] = useState("");
  const isRetiree = context?.demanderKbn === DemanderKbn["退職者本人"];
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  const handleInit = async () => {
    // 持回り情報を初期表示する
    let resArr = stateFakeTableList.current;

    for (let i in context) {
      let targetObj = resArr.find((item) => item.keyword === i)!;

      targetObj &&
        (targetObj.setDefaultValue = context[i as keyof typeof context]);
    }

    let demanderNameKana = resArr.find(
      (item) => item.keyword === "demanderNameKana"
    )!;
    let demanderName = resArr.find((item) => item.keyword === "demanderName")!;
    demanderNameKana.firstNameLabel = isRetiree;
    demanderName.firstNameLabel = isRetiree;
    demanderNameKana.setDefaultValue =
      isRetiree && !context?.demanderNameKana
        ? context?.staffNameKana
        : context?.demanderNameKana;
    demanderName.setDefaultValue =
      isRetiree && !context?.demanderName
        ? context?.staffName
        : context?.demanderName;

    resArr.find((item) => item.keyword === "demanderBirth")!.setDefaultValue =
      isRetiree && !context?.demanderBirth
        ? context?.birthday
        : context?.demanderBirth;

    resArr.find((item) => item.keyword === "demanderZipcode")!.setDefaultValue =
      isRetiree && !context?.demanderZipcode
        ? context?.retirementZipcode
        : context?.demanderZipcode;

    resArr.find((item) => item.keyword === "locationGroup")!.setDefaultValue = {
      prefectures: TkGetCode(
        Prefectures,
        isRetiree && !context?.demanderPrefectureCd
          ? context?.retirementPrefectureCd
          : context?.demanderPrefectureCd
      ),
      city:
        isRetiree && !context?.demanderCity
          ? context?.retirementAddressCity ?? ""
          : context?.demanderCity ?? "",
      address:
        isRetiree && !context?.demanderAddressOthers
          ? context?.retirementAddressOthers ?? ""
          : context?.demanderAddressOthers ?? "",
    }; // デフォルトを設定

    resArr.find((item) => item.keyword === "tel")!.setDefaultValue =
      isRetiree && !context?.tel ? context?.retirementTel : context?.tel;

    // 請求者メールアドレス（機構連絡用）
    let demanderEmail = resArr.find(
      (item) => item.keyword === "demanderEmail"
    )!;
    let demanderEmailConfirm = resArr.find(
      (item) => item.keyword === "demanderEmailConfirm"
    )!;
    let demanderEmailDefValue =
      userType === authorityUserType.userTypeNumber.TAISYOKUSYA
        ? context?.demanderEmail ?? state.authInfo?.email
        : context?.demanderEmail;

    demanderEmail.setDefaultValue = demanderEmailDefValue;
    demanderEmail.requiredIcon = !isHojin;
    demanderEmailConfirm.setDefaultValue = demanderEmailDefValue;
    demanderEmailConfirm.requiredIcon = !isHojin;

    // 代行者連絡先メールアドレス
    let agentEmail = resArr.find((item) => item.keyword === "agentEmail")!;
    let agentEmailConfirm = resArr.find(
      (item) => item.keyword === "agentEmailConfirm"
    )!;
    let agentEmailDefValue = isHojin
      ? context?.agentEmail ?? state.authInfo?.email
      : context?.agentEmail;
    agentEmail.showLine = isHojin;
    agentEmail.setDefaultValue = agentEmailDefValue;
    agentEmailConfirm.showLine = isHojin;
    agentEmailConfirm.setDefaultValue = agentEmailDefValue;

    setStateTableList([...resArr]);
  };

  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let demanderZipcode = resArr.find(
      (item) => item.keyword === "demanderZipcode"
    )!;
    demanderZipcode.getPostalCodeValue = (value: any) => {
      const data = value.value;

      let resObj = {
        prefectures: data.address1,
        city: data.address2,
        address: data.address3,
      };
      resArr.find((item) => item.keyword === "locationGroup")!.valueObject =
        resObj;
      setState({
        ...state,
        errorMsgId: "",
      });
    };
    setStateTableList(resArr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let tel = resArr.find((item) => item.keyword === "tel")!;

    tel.onGetInputValue = (value: any) => {
      setTel(value.target.valueForShow);
    };
  }, [stateTableList]);

  // バリデーションチェックを制御
  const ruleSchema = useMemo(() => {
    let schemaArr = schema;

    // ユーザタイプが法人の場合
    if (isHojin) {
      delete schemaArr.demanderEmail.required;
      delete schemaArr.demanderEmailConfirm.required;
    } else {
      delete schemaArr.agentEmail.required;
      delete schemaArr.agentEmailConfirm.required;
    }

    return schemaArr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const makeHttpObj = (obj: any): IAPI_RET005_Request => {
    const reqObj: IAPI_RET005_Request = {
      ...context,
      fileList: context?.fileList ?? [],
      ...obj,
      tempSaveStep: 3,
      businessType: businessState.common?.businessType,
      contractorNo: isKiko
        ? businessState.common?.contractorNo!
        : state.authInfo?.contractorNo,
      nowZipcode: isRetiree
        ? context?.demanderZipcode
        : context?.retirementZipcode,
      nowPrefectureCd: isRetiree
        ? context?.demanderPrefectureCd
        : context?.retirementPrefectureCd,
      nowAddressCity: isRetiree
        ? context?.demanderCity
        : context?.retirementAddressCity,
      nowAddressOthers: isRetiree
        ? context?.demanderAddressOthers
        : context?.retirementAddressOthers,
      contractorName: businessState.common?.contractorName,
      screenId: "SCRR411106",
    };

    return API_RET005_BuildReqParams(reqObj);
  };

  const handleSubmit = async (data: any, index: any) => {
    const dataObj = {
      demanderNameKana: data.demanderNameKana,
      demanderName: data.demanderName,
      demanderBirth: data.demanderBirth,
      demanderZipcode: data.demanderZipcode,
      demanderPrefectureCd:
        Prefectures[data.locationGroup.prefectures as keyof typeof Prefectures],
      demanderCity: data.locationGroup.city,
      demanderAddressOthers: data.locationGroup.address,
      tel,
      demanderEmail: data.demanderEmail,
      agentEmail: isHojin ? data.agentEmail : undefined,
      retirementReason: data.retirementReason,
    };

    switch (index) {
      case 0: // 一時保存
        // 退職手当金請求更新API実行
        try {
          const res = await httpAPI_RET005({
            ...makeHttpObj(dataObj),
          });

          if (res.resultCd === "I0000") {
            setState({
              ...state,
              errorMsgId: "",
            });
            setBusinessState({
              ...businessState,
              job_35: {
                ...context,
                ...dataObj,
                tempSaveStep: 3,
                applyId: res.applyId,
              },
            });
            setOpen(true);
          } else {
            setState({
              ...state,
              errorMsgId: res.resultCd,
            });
          }
        } catch (error) {
          navigate("/error");
        }
        break;

      case 1: // 次へ
        // 業務チェック
        let errorMsgId = "";
        if (
          isHojin &&
          (data.demanderEmail !== data.demanderEmailConfirm ||
            data.agentEmail !== data.agentEmailConfirm)
        ) {
          errorMsgId = "TKMSG_SCR0001E";
        }
        if (!isRetiree && data.demanderName === context?.staffNameKanji) {
          errorMsgId = "TKMSG_SCR0037E";
        }
        setState({
          ...state,
          errorMsgId,
        });
        if (errorMsgId) return;

        setBusinessState({
          ...businessState,
          job_35: {
            ...context,
            ...dataObj,
          },
        });

        if (!isRetiree && data.demanderBirth === context?.birthday) {
          setWarningOpen(true);
          return;
        }
        if (isHojin && !data.demanderEmail && !data.demanderEmailConfirm) {
          setEmailWarningOpen(true);
          return;
        }

        navigate("/SCRR411107");
        break;
    }
  };

  const onOkyWarning = () => {
    setWarningOpen(false);
    setEmailWarningOpen(false);
    navigate("/SCRR411107");
  };

  return (
    <Box data-testid="SCRR411106test">
      <TkStepper
        steps={steps}
        savaStep={context?.tempSaveStep}
        activeStep={2}
      />

      <TkInfoManual rule="\n">{labelids.labelid.LBLR411106_01}</TkInfoManual>

      {isKiko && (
        <TkContractorAndNumberLine
          name="退職者名"
          no={context?.staffNameKanji!}
        />
      )}

      <TkFakeTable
        list={stateTableList}
        ruleSchema={ruleSchema}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
        buttonGroup={<TkBackButton />}
        onCheckAndSubmit={(e, index) => handleSubmit(e, index)}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
      <TkMessageDialog
        open={warningOpen}
        onOky={onOkyWarning}
        onCancel={() => setWarningOpen(false)}
        title="警告"
        contextText={TkPropertyMessageGet("TKMSG_SCR0007W")}
        okText="はい"
        cancelText="いいえ"
      />
      <TkMessageDialog
        open={emailWarningOpen}
        onOky={onOkyWarning}
        onCancel={() => setEmailWarningOpen(false)}
        title="警告ダイアログ"
        okText="次へ"
        cancelText="戻る"
        children={<TkInfoManual rule={"<"}>{contextText}</TkInfoManual>}
      />
    </Box>
  );
};

export default SCRR411106;
